export const editorGridColumnMinimum = 1;
export const editorGridColumnMaximum = 20;
export const editorGridDefaultColumns = 12;
export const editorGridHeightStep = 8;
export const editorImageScaleMinimum = 25;
export const editorImageScaleMaximum = 500;
export const editorImageBreakpointKeys = ['full-hd', 'macbook-pro', 'desktop', 'tablet', 'mobile'] as const;
export type WebEditorImageBreakpointKey = (typeof editorImageBreakpointKeys)[number];
export type WebEditorImageScaleMap = Partial<Record<WebEditorImageBreakpointKey, number>>;

export interface WebEditorImageScaleSource {
  imageScaleByBreakpoint?: WebEditorImageScaleMap;
  imageScaleDesktop?: number;
  imageScaleMobile?: number;
}

export interface WebEditorGridPlacement {
  columnStart: number;
  columnSpan: number;
  order: number;
  maskWidth?: number;
  maskHeight?: number;
  parentId?: string;
}

export interface WebEditorFocalPoint {
  x: number;
  y: number;
}

interface WebEditorRect {
  left: number;
  width: number;
  height: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const finiteNumber = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : undefined);

export const clampEditorGridColumns = (value: number) =>
  Math.min(editorGridColumnMaximum, Math.max(editorGridColumnMinimum, Math.round(value)));

export const toggleEditorGeometryLock = (unlockedElementIds: ReadonlySet<string>, elementId: string) => {
  const next = new Set(unlockedElementIds);
  if (next.has(elementId)) next.delete(elementId);
  else next.add(elementId);
  return next;
};

export const clampEditorGridHeight = (value: number) =>
  Math.min(2400, Math.max(48, Math.round(value / editorGridHeightStep) * editorGridHeightStep));

export const clampEditorGridWidth = (value: number) => Math.min(2400, Math.max(24, Math.round(value)));

export const clampEditorFocalPoint = (value: number) => Math.min(100, Math.max(0, Math.round(value)));

export const clampEditorImageScale = (value: number) =>
  Math.min(editorImageScaleMaximum, Math.max(editorImageScaleMinimum, Math.round(value)));

export const normalizeEditorImageScaleMap = (value: unknown): WebEditorImageScaleMap | undefined => {
  if (!isRecord(value)) return undefined;
  const result: WebEditorImageScaleMap = {};
  editorImageBreakpointKeys.forEach(key => {
    const size = finiteNumber(value[key]);
    if (size !== undefined) result[key] = clampEditorImageScale(size);
  });
  return Object.keys(result).length ? result : undefined;
};

export const resolveEditorImageScale = (frame: WebEditorImageScaleSource, breakpoint: WebEditorImageBreakpointKey) =>
  frame.imageScaleByBreakpoint?.[breakpoint] ??
  (breakpoint === 'mobile' ? frame.imageScaleMobile ?? frame.imageScaleDesktop : frame.imageScaleDesktop) ?? 100;

export const normalizeEditorGridPlacement = (
  value: unknown,
  columns = editorGridDefaultColumns,
): WebEditorGridPlacement | undefined => {
  if (!isRecord(value)) return undefined;
  const normalizedColumns = clampEditorGridColumns(columns);
  const rawStart = finiteNumber(value.columnStart);
  const rawSpan = finiteNumber(value.columnSpan);
  const rawOrder = finiteNumber(value.order);
  if (rawStart === undefined || rawSpan === undefined || rawOrder === undefined) return undefined;
  const columnStart = Math.min(normalizedColumns, Math.max(1, Math.round(rawStart)));
  const columnSpan = Math.min(normalizedColumns - columnStart + 1, Math.max(1, Math.round(rawSpan)));
  const maskWidth = finiteNumber(value.maskWidth);
  const maskHeight = finiteNumber(value.maskHeight);
  return {
    columnStart,
    columnSpan,
    order: Math.max(0, Math.round(rawOrder)),
    ...(maskWidth === undefined ? {} : { maskWidth: clampEditorGridWidth(maskWidth) }),
    ...(maskHeight === undefined ? {} : { maskHeight: clampEditorGridHeight(maskHeight) }),
    ...(typeof value.parentId === 'string' && value.parentId ? { parentId: value.parentId } : {}),
  };
};

export const editorGridPlacementFromRects = (
  nodeRect: WebEditorRect,
  parentRect: WebEditorRect,
  columns: number,
  order: number,
  parentId: string,
  isImage: boolean,
  imageScale = 1,
): WebEditorGridPlacement => {
  const width = nodeRect.width / Math.max(0.01, imageScale);
  const height = nodeRect.height / Math.max(0.01, imageScale);
  const left = nodeRect.left + (nodeRect.width - width) / 2;
  const relativeLeft = Math.max(0, left - parentRect.left);
  const columnStart = Math.min(columns, Math.max(1, Math.round((relativeLeft / parentRect.width) * columns) + 1));
  const columnSpan = Math.min(columns - columnStart + 1, Math.max(1, Math.round((width / parentRect.width) * columns)));
  return {
    columnStart,
    columnSpan,
    order,
    parentId,
    ...(isImage ? { maskWidth: clampEditorGridWidth(width), maskHeight: clampEditorGridHeight(height) } : {}),
  };
};

export const normalizeEditorFocalPoint = (value: unknown): WebEditorFocalPoint | undefined => {
  if (!isRecord(value)) return undefined;
  const x = finiteNumber(value.x);
  const y = finiteNumber(value.y);
  return x === undefined || y === undefined
    ? undefined
    : { x: clampEditorFocalPoint(x), y: clampEditorFocalPoint(y) };
};
